// src/pages/forge-ultra-dashboard.tsx
//
// The main internal platform page the user lands on after the
// /login-success handshake. Authentication / authorisation is enforced
// one level up by <ProtectedRoute /> in src/App.tsx (which wraps the
// /dashboard route), so this component is pure internal UI — it can
// safely assume the caller is a Premium / Professional member or an
// administrator.
//
// Layout follows a Top/Middle/Bottom narrative flow:
//   TIER 1 — Macroeconomic Context     ("Where are we?")
//   TIER 2 — Asset Class Opportunities ("What is performing?")
//   TIER 3 — Execution & Risk Tools    ("What do I do about it?")
//   DEEP DIVE — Historical macro trajectory + interactive tilt.
//
// The asset-ranking routes (/dashboard/stock-ranking, etc.) and
// /dashboard/portfolio-optimization are registered in src/App.tsx:
// stock-ranking renders the live StockRankingPage; the others render
// the shared <ComingSoon /> placeholder until their modules ship.

import { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

import { GlobalSearchBar } from '../features/global-search';
import { PerformanceDashboard } from '../features/market-analysis/index-performance-analytics';
import {
    CycleTrajectory,
    MacroCompass,
    RegimeAlertWidget,
    RegimeTilt,
    TiltedAllocationCard,
} from '../features/macro-regime';
import { ActionBanners, Layout, Sidebar } from '../shared/ui';

export default function Dashboard() {
    // Anchor for the "Review Tilted Allocation" CTA on the AFML
    // Regime Alert widget. Wrapping the new TiltedAllocationCard in a
    // ref'd div is cheaper than threading a forwardRef through the
    // feature module and keeps the scroll target colocated with the
    // dashboard layout. The button scrolls the user to the donut
    // visualisation of the cycle-driven tilted allocation, which sits
    // below the interactive RegimeTilt bar chart.
    const tiltedAllocationRef = useRef<HTMLDivElement | null>(null);
    const scrollToTiltedAllocation = useCallback(() => {
        tiltedAllocationRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Forge Ultra Wealth-Building Platform"
            subtitle="Live quantitative intelligence and portfolio execution."
        >
            {/* * ==========================================
              * GLOBAL SEARCH: platform-wide asset command palette.
              * Self-contained island — debounced, keyboard-navigable,
              * routes straight to the matching deep-dive page.
              * ==========================================
              */}
            <div className="mb-10">
                <GlobalSearchBar />
            </div>

            {/* * ==========================================
              * NEW CTA BANNERS: High-visibility, above-the-fold premium
              * quick-links to Market Intelligence + Actionable Insights.
              * ==========================================
              */}
            <ActionBanners />

            {/* * ==========================================
              * MARKET PERFORMANCE: Global indices + commodities/crypto.
              * Purely presentational — the ARQ worker pre-computes every
              * metric (5Y range, YTD, Numba slider position) and the
              * /market-analysis/index-performance/latest endpoint serves
              * the latest snapshot per symbol via a DISTINCT ON lookup.
              * ==========================================
              */}
            <section aria-label="Global market performance" className="mb-12">
                <PerformanceDashboard />
            </section>

            {/* * ==========================================
              * TIER 1: The Macro Environment (Top Row)
              * ==========================================
              */}
            <section aria-label="Macro regime outlook" className="space-y-6">
                <header>
                    <h2 className="section-title">Macroeconomic Outlook</h2>
                    <p className="muted text-sm mt-1">
                        Live AFML(Advanced Financial Machine Learning) regime classification and cycle transition risk estimates.
                    </p>
                </header>
                <div className="grid gap-6 xl:grid-cols-2">
                    {/* Your exact components, untouched */}
                    <MacroCompass />
                    <RegimeAlertWidget onReviewPortfolio={scrollToTiltedAllocation} />
                </div>
            </section>

            {/* * ==========================================
              * TIER 2: Asset Intelligence (Middle Row)
              * ==========================================
              */}
            <section aria-label="Asset Intelligence" className="mt-12 space-y-6">
                <header>
                    <h2 className="section-title">Asset Class Opportunities</h2>
                    <p className="muted text-sm mt-1">
                        Top-ranked assets across categories based on Momentum, Quality, Sentiment, and Forge Ultra Signal.
                    </p>
                </header>

                <div className="grid gap-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                    {[
                        { label: 'Equities', to: '/dashboard/stock-ranking', color: 'text-blue-500 dark:text-blue-400' },
                        { label: 'ETFs', to: '/dashboard/etf-ranking', color: 'text-purple-500 dark:text-purple-400' },
                        { label: 'Commodities', to: '/dashboard/commodities-ranking', color: 'text-amber-500 dark:text-amber-400' },
                        { label: 'REITs', to: '/dashboard/reits-ranking', color: 'text-emerald-500 dark:text-emerald-400' },
                        { label: 'Crypto', to: '/dashboard/crypto-ranking', color: 'text-orange-500 dark:text-orange-400' },
                        { label: 'FX & Currencies', to: '/dashboard/fx-ranking', color: 'text-cyan-500 dark:text-cyan-400' },
                    ].map((asset) => (
                        <Link key={asset.to} to={asset.to} className="surface-card group flex flex-col items-center justify-center py-6 text-center hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer border border-slate-200 dark:border-slate-800">
                            <span className={`text-3xl mb-2 font-black ${asset.color}`}>Top Ranked</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-green-600 dark:group-hover:text-emerald-400 transition-colors">
                                {asset.label}
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* * ==========================================
              * TIER 3: Execution & Risk Tools (Bottom Row)
              * ==========================================
              */}
            <section aria-label="Execution and Risk" className="mt-12 space-y-6">
                <div className="grid gap-6 xl:grid-cols-2">
                    {/* Left Column: Risk Tools */}
                    <div className="space-y-6 flex flex-col h-full">
                        <header>
                            <h2 className="section-title">Risk Intelligence</h2>
                            <p className="muted text-sm mt-1">Know yourself as an investor. Understand how your portfolio may behave under pressure. Stay aligned with your long-term goals through personalized risk insights, portfolio stress testing, and goal-based risk analysis.</p>
                        </header>
                        <div className="grid gap-4 sm:grid-cols-2 flex-1">
                            <Link to="/dashboard/risk-assessment" className="surface-card group hover:border-emerald-500/50 transition-colors cursor-pointer flex flex-col justify-center">
                                <h3 className="font-bold text-slate-900 dark:text-slate-100 group-hover:text-emerald-500 transition-colors">Risk Assessment</h3>
                                <p className="text-xs muted mt-1">Update capacity and behavioral biases.</p>
                            </Link>
                            <Link to="/dashboard/portfolio-stress-test" className="surface-card group hover:border-emerald-500/50 transition-colors cursor-pointer flex flex-col justify-center">
                                <h3 className="font-bold text-slate-900 dark:text-slate-100 group-hover:text-emerald-500 transition-colors">Scenario Simulator</h3>
                                <p className="text-xs muted mt-1">Stress test your recommended allocation.</p>
                            </Link>
                        </div>
                    </div>

                    {/* Right Column: Portfolio Optimizer */}
                    <div className="space-y-6 flex flex-col h-full">
                        <header>
                            <h2 className="section-title">Portfolio Engine</h2>
                            <p className="muted text-sm mt-1">Build a portfolio aligned with your goals using our Smart Stock Portfolio Builder or Multi-Asset Optimizer, and discover data-driven ideas to strengthen your investment strategy.</p>
                        </header>
                        <Link
                            to="/dashboard/stock-only-portfolio"
                            className="flex-1 surface-card group flex flex-col items-center justify-center p-8 bg-gradient-to-br from-slate-100 to-emerald-50/30 dark:from-slate-900 dark:to-emerald-900/10 border-emerald-200 dark:border-emerald-800/30 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors cursor-pointer"
                        >
                            <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 group-hover:text-emerald-500 transition-colors">Stock-Only Portfolio</h3>
                            <p className="text-sm muted mt-2 text-center max-w-xs">Run Hierarchical Risk Parity (HRP) to build a robust, tilt-aligned portfolio.</p>
                        </Link>
                        <Link
                            to="/dashboard/multi-asset-portfolio-optimizer"
                            className="flex-1 surface-card group flex flex-col items-center justify-center p-8 bg-gradient-to-br from-slate-100 to-indigo-50/30 dark:from-slate-900 dark:to-indigo-900/10 border-indigo-200 dark:border-indigo-800/30 hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors cursor-pointer"
                        >
                            <div className="h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 3v9l6 3"/></svg>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 group-hover:text-indigo-500 transition-colors">Multi-Asset Optimizer</h3>
                            <p className="text-sm muted mt-2 text-center max-w-xs">Interactive 3-tier cross-asset What-If cockpit with a dynamic macro-regime overlay.</p>
                        </Link>
                    </div>
                </div>
            </section>

            {/* * ==========================================
              * DEEP DIVE: Historical Macro & Tilt (Below Fold)
              * ==========================================
              */}
            <section aria-label="Macro deep dive" className="mt-16 pt-10 border-t border-slate-300/70 dark:border-slate-800/70 space-y-6">
                <header>
                    <h2 className="section-title">Macro Deep Dive</h2>
                    <p className="muted text-sm mt-1">Historical trajectory and interactive asset allocation tilt parameters.</p>
                </header>
                <CycleTrajectory months={12} />
                <div id="regime-tilt" className="scroll-mt-24">
                    <RegimeTilt />
                </div>
                {/* Scroll target triggered by the RegimeAlertWidget CTA */}
                <div ref={tiltedAllocationRef} id="tilted-allocation" className="scroll-mt-24">
                    <TiltedAllocationCard />
                </div>
            </section>
        </Layout>
    );
}